import { ApplicationTargetEnum } from '~types/app-selection';

/**
 * The CMS already declares a complete design-system token sheet in
 * `src/styles/_design-tokens.scss` (Primary, Neutral, spacing, radius,
 * z-index, …). Those tokens describe the CMS chrome and don't change
 * between BUs.
 *
 * The tokens declared here describe what *does* change when the CMS user
 * switches the simulated mobile preview between Couche-Tard (QC) and
 * Circle K (CA): the brand color used inside the phone, which logo to
 * render in the app header, and which bottom-tab entries are visible.
 *
 * They are intentionally narrow — anything that's the same across BUs
 * stays in the global SCSS tokens and is consumed via `var(--Primary-6)`,
 * `var(--S)`, etc.
 */
export type PreviewTheme = {
    region: PreviewRegion;
    colors: {
        // Brand color used inside the simulated app (header background,
        // active tab inkbar, primary CTA). Maps to `--preview-primary` once
        // injected by ThemeProvider.
        primary: string;
        // Foreground color readable on top of `primary` (typically white).
        primaryContrast: string;
    };
    logo: {
        // Identifier consumed by `<PreviewBrandLogo />` to pick the right
        // SVG. We don't ship the asset reference here so the theme module
        // stays free of React/SVG imports.
        id: PreviewLogoId;
    };
    // Ordered list of bottom-tab entries shown for this BU. The actual
    // labels and icons are resolved by `<PreviewBottomTabBar />` from i18n
    // and the icon registry.
    tabs: readonly PreviewTabKey[];
};

export type PreviewRegion = ApplicationTargetEnum;

export type PreviewLogoId = 'CT' | 'CK';

export type PreviewTabKey =
    | 'home'
    | 'map'
    | 'products'
    | 'carwash'
    | 'age-gated'
    | 'more';

/**
 * CSS custom properties injected by `<PreviewThemeProvider />`. They live
 * under the `.preview-theme-root` scope so they don't leak into the rest
 * of the CMS. Component SCSS modules consume them via `var(--preview-…)`.
 */
export const PREVIEW_CSS_VARS = {
    primary: '--preview-primary',
    primaryContrast: '--preview-primary-contrast',
} as const;

export type PreviewCssVarName = typeof PREVIEW_CSS_VARS[keyof typeof PREVIEW_CSS_VARS];
